import { useQueryClient } from '@tanstack/react-query';
import { createRootRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { BetaBanner } from '@/components/beta-banner';
import { getArenaImmersive, subscribeArenaChrome } from '@/stores/arena-chrome';
import { Button, buttonVariants } from '@/components/ui/button';
import { useGoogleAuthLanding } from '@/hooks/use-google-auth-landing';
import { NativeShell } from '@/native/NativeShell';
import { clearSessionId } from '@/native/session';
import { useSession } from '@/hooks/use-session';
import { trpc } from '@/lib/trpc';
import { onIdentityChanged } from '@/lib/session-change';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme.store';

export const Route = createRootRoute({
  component: RootLayout,
});

/** Matches `ADMIN_ROLE` on the server; here it only decides whether the nav link is drawn. */
const ADMIN_ROLE = 'admin';

function RootLayout() {
  // Google sends the browser back to WEB_APP_URL, whichever route that is, so the landing is
  // picked up here rather than on any one page.
  useGoogleAuthLanding();

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  /**
   * The arena takes the whole window while somebody is actually playing in it.
   *
   * Both rows go, not just the app bar: the beta strip is the other thing above `<Outlet />`, and
   * "the map/grid should take up the full top" is a statement about the top of the window rather
   * than about one element. The arena used to draw a beta badge of its own in the HUD's top-left
   * corner; the player asked for that corner back — "the beta badge should be in the top header"
   * — so this strip is now the only place the app says it, and a live match says nothing.
   *
   * **IT IS A LIVE MATCH, NOT THE ROUTE**, which is why this is a store read and not
   * `isArenaRoute`: a dead player is still on `/play` and gets the shell back with the death
   * panel, because at that moment they are choosing what to do next rather than steering. See
   * `stores/arena-chrome.ts` for why one boolean is a singleton, and for the three ways out of
   * the arena that make hiding the navigation safe.
   */
  const immersive = useSyncExternalStore(subscribeArenaChrome, getArenaImmersive, () => false);

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      {/* The Capacitor shell's back button, status bar and exit prompt. Nothing at all on web. */}
      <NativeShell />
      {!immersive && (
        <>
          <Header />
          <BetaBanner />
        </>
      )}
      {/*
       * The bottom safe-area inset, spent exactly once — see `.safe-page-bottom` in `app.css`.
       * The document is the scroll container for every ordinary route, so this is what lets a
       * long page scroll clear of Android's gesture bar and iOS's home indicator. The arena is
       * the exception: there the thing against the bottom edge is the HUD overlay or the action
       * footer, which own the inset themselves.
       */}
      <main
        className={cn(
          'flex flex-1 flex-col',
          !isArenaRoute(pathname) && 'safe-page-bottom safe-page-x',
        )}
      >
        <Outlet />
      </main>
    </div>
  );
}

/** `/play` and nothing else; the arena divides the three remaining edges up for itself. */
function isArenaRoute(pathname: string): boolean {
  return pathname === '/play' || pathname.startsWith('/play/');
}

/**
 * Below this the app bar cannot hold its row, and collapses into a menu.
 *
 * `639px` is Tailwind's `sm` breakpoint minus one, spelled out because this is a `matchMedia`
 * query rather than a class — the same arithmetic `TOUCH_CONTROLS_QUERY` does in the HUD, and
 * for the same reason: the two variants must never both be in the DOM. A CSS-only `hidden
 * sm:flex` pair would put every nav link on the page twice, which is a duplicate landmark for a
 * screen reader and an ambiguous match for anything that looks a link up by name.
 */
const COMPACT_HEADER_QUERY = '(max-width: 639px)';

function useCompactHeader(): boolean {
  const [compact, setCompact] = useState(matchesCompactHeader);

  useEffect(() => {
    // jsdom has no `matchMedia`, and neither does a server render: both get the full bar.
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(COMPACT_HEADER_QUERY);
    const onChange = () => setCompact(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return compact;
}

function matchesCompactHeader(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(COMPACT_HEADER_QUERY).matches;
}

/**
 * The app bar.
 *
 * **It fits, at every width, and that is the whole of the layout rule.** It used to be one
 * unbreakable row — wordmark, four nav links, a theme toggle and two auth controls — with no
 * wrapping, no scrolling and nothing hidden, which measures around 480px against the 328px a
 * 360px phone has left after the side insets. The overflow went off the right-hand edge, took
 * the sign-in controls with it and gave the whole document a horizontal scroll: "the header is
 * off screen".
 *
 * Under `sm` the links and the auth controls therefore move into a menu, leaving a row that is
 * the wordmark, the theme toggle and one button — about 210px, which fits an iPhone SE. At `sm`
 * and up the bar is exactly what it always was. The top and side safe-area insets are
 * `.safe-header`'s (see `app.css`); this file owns only what goes in the row.
 *
 * **WHO YOU ARE IS ON THE RIGHT, AS A FACE.** A registered player's account lives behind their
 * own picture in the top right — the place every application on a phone puts it — rather than
 * behind a "Profile" link filed among the site's navigation on the left. That is the only
 * control that moved: `NavLinks` is now the *places* this app has (Play, Leaderboard, and the
 * console for a role that has one) and `AccountMenu` is *you*.
 */
function Header() {
  const { user, isLoading } = useSession();
  const avatarUrl = useAvatarUrl(user);
  const compact = useCompactHeader();
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Two ways the panel stops being the right thing to draw, and neither is a click on it: the
  // route changed under it, or the window grew back past the breakpoint. Both are dependencies
  // this effect reads for the change alone, which is why neither appears in its body.
  useEffect(() => setMenuOpen(false), [pathname, compact]);

  const registered = user !== null && !user.isGuest;

  return (
    <header className="safe-header sticky top-0 z-40 flex shrink-0 items-center gap-3 border-b border-border bg-header text-header-foreground sm:gap-4">
      <Link
        to="/"
        className="flex min-w-0 items-center gap-2 text-lg font-black tracking-tight"
        onClick={() => setMenuOpen(false)}
      >
        <span aria-hidden="true">🟢</span>
        <span className="truncate">Chompdome</span>
      </Link>

      {compact ? (
        // Deliberately unpositioned: `SiteMenu`'s panel is absolute and hangs off the header,
        // which is the nearest containing block, so `top-full` means "under the app bar" rather
        // than "under these two buttons".
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            // Above the panel's backdrop, so the button that opened it is the button that
            // closes it rather than a target the backdrop has swallowed.
            className={cn('relative z-40', registered && 'overflow-hidden rounded-full p-0')}
            // The label does not change with the face behind it: this is the one control that
            // opens the one menu at this width, whether it is drawn as a portrait or as a
            // hamburger, and a screen reader should hear the same thing either way.
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {registered ? (
              <Avatar name={user.displayName} url={avatarUrl} />
            ) : (
              <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
            )}
          </Button>
          {menuOpen && (
            <SiteMenu user={user} isLoading={isLoading} onNavigate={() => setMenuOpen(false)} />
          )}
        </div>
      ) : (
        <>
          <NavLinks role={user?.role} />

          <div className="ml-auto flex shrink-0 items-center gap-3">
            <ThemeToggle />
            {isLoading ? null : registered ? (
              <AccountMenu user={user} avatarUrl={avatarUrl} />
            ) : user ? (
              <GuestControls name={user.displayName} />
            ) : (
              <SignedOutLinks />
            )}
          </div>
        </>
      )}
    </header>
  );
}

/**
 * The player's own profile picture, or null.
 *
 * `auth.me` answers who you are and says nothing about what you look like — the picture is a
 * column on `profile.me`, which is the caller's own row and a primary-key lookup. It is asked
 * for only when there is a registered account to ask about: `profile.me` is a
 * `protectedProcedure`, and a guest owns no uploaded objects at all (see the uploads router's
 * refusal), so a guest's request could only ever come back empty.
 *
 * The profile page invalidates this query when a new picture lands, which is what makes the face
 * in the corner change without a reload.
 */
function useAvatarUrl(user: SessionUser): string | null {
  const registered = user !== null && !user.isGuest;
  const me = trpc.profile.me.useQuery(undefined, {
    enabled: registered,
    // A picture changes when its owner uploads one, and that path invalidates. Re-asking on
    // every navigation would be a query per page for a value that almost never moves.
    staleTime: 60_000,
  });

  return registered ? (me.data?.avatarUrl ?? null) : null;
}

/**
 * The nav, as one list, so the bar and the menu can never disagree about what is in it.
 *
 * Places only. "Profile" used to sit here and does not any more: it is not somewhere the app
 * goes, it is the account, and the account is the face on the right (`AccountMenu`, and the
 * session block of `SiteMenu` at compact widths).
 */
function NavLinks({
  role,
  stacked = false,
  onNavigate,
}: {
  role?: string;
  stacked?: boolean;
  onNavigate?: () => void;
}) {
  const linkClass = stacked
    ? 'rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted'
    : 'text-muted-foreground transition-colors hover:text-foreground';

  return (
    <nav className={stacked ? 'flex flex-col' : 'flex items-center gap-4 text-sm font-medium'}>
      <Link to="/play" className={linkClass} onClick={onNavigate}>
        Play
      </Link>
      <Link to="/leaderboard" className={linkClass} onClick={onNavigate}>
        Leaderboard
      </Link>
      {role === ADMIN_ROLE && (
        <Link to="/admin" className={linkClass} onClick={onNavigate}>
          Admin
        </Link>
      )}
    </nav>
  );
}

/** Exactly what `useSession` answers with, so the menu cannot drift from the bar's own idea. */
type SessionUser = ReturnType<typeof useSession>['user'];

/**
 * A round face, with two fallbacks under it.
 *
 * The picture is loaded from the uploads bucket, which is a different origin that can 404, be
 * blocked, or answer without the CORS header — so `onError` is not an edge case, it is the
 * ordinary state of a deployment whose bucket policy is half configured (see the skins card).
 * What is drawn then is the player's initials, which is a *name*, and therefore still identifies
 * the account rather than leaving a broken-image glyph in the app bar.
 *
 * The failure is remembered against the URL rather than as a plain boolean, so uploading a new
 * picture after a broken one gets a fresh attempt without an effect to reset anything.
 */
function Avatar({ name, url }: { name: string; url: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = url !== null && failedUrl !== url;

  return (
    <span
      data-testid="account-avatar"
      className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-xs font-bold uppercase text-secondary-foreground"
    >
      {showImage ? (
        // Decorative: the control around it is already labelled with the account it belongs to.
        <img
          src={url}
          alt=""
          className="size-full object-cover"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </span>
  );
}

/**
 * Up to two letters off a display name.
 *
 * Spread rather than `charAt`, because a name may open with an emoji or any other astral
 * character and half a surrogate pair renders as a replacement glyph. A name that is empty or
 * all whitespace still has to draw something, so it gets a question mark.
 */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((word) => [...word][0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * The account menu: the face in the top right, and what is behind it.
 *
 * A `role="menu"` popup rather than a second `<nav>`, which is what makes it acceptable for
 * "Leaderboard" and "Admin" to appear both here and in the bar's own navigation — a menu is not
 * a landmark, so a screen reader is offered one navigation region and one menu rather than two
 * competing lists of the same links. Everything a player does *to their account* is in it,
 * including the way out.
 *
 * Only ever drawn at `sm` and up: below that the same items are in `SiteMenu`, behind the same
 * face, because a phone has room for exactly one control in that corner.
 */
function AccountMenu({
  user,
  avatarUrl,
}: {
  user: NonNullable<SessionUser>;
  avatarUrl: string | null;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Back to where the keyboard was: dismissing a menu must not drop focus onto the body.
      triggerRef.current?.focus();
    };
    // `pointerdown` rather than `click`, so a press that lands on the page closes the menu
    // before whatever it landed on decides what to do about it.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const itemClass =
    'rounded-lg px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-muted';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        // The name is the label, so a screen reader says whose account this is rather than
        // "button". `aria-haspopup` is what tells it there is a menu behind the face.
        aria-label={`Account menu for ${user.displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={user.displayName} url={avatarUrl} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="account-menu"
          aria-label="Account"
          className="absolute right-0 top-full z-50 mt-2 flex w-56 flex-col gap-1 rounded-xl border border-border bg-card p-2 text-card-foreground shadow-xl"
        >
          <p className="truncate px-3 py-1 text-sm text-muted-foreground">{user.displayName}</p>

          <Link to="/profile" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
            Profile
          </Link>
          <Link
            to="/leaderboard"
            role="menuitem"
            className={itemClass}
            onClick={() => setOpen(false)}
          >
            Leaderboard
          </Link>
          {user.role === ADMIN_ROLE && (
            <Link to="/admin" role="menuitem" className={itemClass} onClick={() => setOpen(false)}>
              Admin
            </Link>
          )}

          <div className="my-1 h-px bg-border" />

          <SignOutButton
            role="menuitem"
            className="w-full justify-start px-3"
            onSignedOut={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The compact bar's menu: everything the row gave up, stacked and thumb-sized.
 *
 * Anchored to the header (`absolute top-full right-0`) rather than drawn as a dialog — it is
 * navigation, not a decision, and a full-screen modal for four links is a heavier thing to
 * dismiss than the one it replaces. The backdrop is what makes a tap anywhere else close it,
 * and it sits *under* the panel in the stacking order so it never eats the tap that opened it.
 *
 * It carries the account items too (Profile, and the way out), because at this width the face in
 * the corner *is* this button — there is no second popup for `AccountMenu` to be.
 */
function SiteMenu({
  user,
  isLoading,
  onNavigate,
}: {
  user: SessionUser;
  isLoading: boolean;
  onNavigate: () => void;
}) {
  const stackedLinkClass =
    'rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted';

  return (
    <>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="fixed inset-0 z-30 cursor-default"
        onClick={onNavigate}
      />
      <div
        id="site-menu"
        data-testid="site-menu"
        className="absolute right-0 top-full z-40 mr-[max(0.5rem,var(--chomp-safe-right))] mt-1 flex w-56 max-w-[calc(100vw-1rem)] flex-col gap-1 rounded-xl border border-border bg-card p-2 text-card-foreground shadow-xl"
      >
        <NavLinks role={user?.role} stacked onNavigate={onNavigate} />

        <div className="my-1 h-px bg-border" />

        {isLoading ? null : user ? (
          <>
            <p className="px-3 py-1 text-sm text-muted-foreground">
              {user.displayName}
              {user.isGuest && <span className="ml-1.5 text-xs">(guest)</span>}
            </p>
            {user.isGuest ? (
              // A guest has no profile page worth the name and no picture to put on it; what
              // they have is the offer to keep what they have built. Same call to action the
              // signed-out visitor gets, minus the "Sign in" they are already past.
              <Link
                to="/register"
                className={buttonVariants({ className: 'mt-1 w-full' })}
                onClick={onNavigate}
              >
                Sign up
              </Link>
            ) : (
              <Link to="/profile" className={stackedLinkClass} onClick={onNavigate}>
                Profile
              </Link>
            )}
            <SignOutButton className="w-full justify-start px-3" onSignedOut={onNavigate} />
          </>
        ) : (
          <>
            <Link to="/login" className={stackedLinkClass} onClick={onNavigate}>
              Sign in
            </Link>
            <Link
              to="/register"
              className={buttonVariants({ className: 'mt-1 w-full' })}
              onClick={onNavigate}
            >
              Sign up
            </Link>
          </>
        )}
      </div>
    </>
  );
}

function SignedOutLinks() {
  return (
    <>
      <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground">
        Sign in
      </Link>
      <Link to="/register" className={buttonVariants({ size: 'sm' })}>
        Sign up
      </Link>
    </>
  );
}

/**
 * A guest, who is signed in and is nobody in particular.
 *
 * No face and no account menu: a guest row is reclaimed by a device id anybody can send, owns no
 * uploaded picture and has no profile page to open. What they get is the call to action —
 * `/register` upgrades the row in place, keeping the name and every statistic — and the way out,
 * which is the one thing they would otherwise lose by not having a menu.
 *
 * The name is still printed, because it is the one the server invented for them and it is what
 * they are about to see over their own blob.
 */
function GuestControls({ name }: { name: string }) {
  return (
    <>
      <span className="max-w-32 truncate text-sm text-muted-foreground">
        {name}
        <span className="ml-1.5 text-xs">(guest)</span>
      </span>
      <Link to="/register" className={buttonVariants({ size: 'sm' })}>
        Sign up
      </Link>
      <SignOutButton />
    </>
  );
}

/**
 * Signing out, in one place, because the compact menu needs the same button the bar has.
 *
 * The mutation lives here rather than in either caller: only one of the two is ever mounted, so
 * hoisting it would be a hook whose owner is neither of the things that use it.
 */
function SignOutButton({
  className,
  role,
  onSignedOut,
}: {
  className?: string;
  role?: string;
  onSignedOut?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = trpc.auth.logout.useMutation({
    onSuccess: async () => {
      // The server has deactivated the row; the shell has to drop its copy of the id too. A
      // no-op in a browser, where the cookie the server just cleared was the whole session —
      // and not optional on a device, because an iOS Keychain entry outlives an uninstall and
      // would sign the next install in as whoever was here before.
      await clearSessionId();
      // Signing out is an identity change like any other, and the one where leaving the last
      // account's answers in the cache is worst: the next person to sign in on this tab would
      // be shown them.
      await onIdentityChanged(queryClient);
      onSignedOut?.();
      navigate({ to: '/' });
    },
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      role={role}
      className={className}
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
    >
      {logout.isPending ? 'Signing out…' : 'Sign out'}
    </Button>
  );
}

function ThemeToggle() {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'dark' ? '☀️' : '🌙'}
    </Button>
  );
}
